import { useEffect, useMemo, useRef, useState } from 'react';
import type { RouteResult, PlannedPoint } from '../../shared/contracts.js';
import type { SelectedLocation } from './PlaceSearch.js';
import { loadGoogleMaps } from '../google.js';
import { errorDetail, track } from '../telemetry.js';
import { reportUnexpected } from '../observed-request.js';
import { mapMarkers } from '../map-markers.js';
import { describeStretch, SIGNAL_MIN_ZOOM, SIGNAL_PING_MS, signalMarkers, SPEED_BANDS, speedColor, speedStretches, UNKNOWN_SPEED_COLOR } from '../route-features.js';
import { createSignalOverlay, type SignalOverlay } from '../signal-overlay.js';

const NZ_CENTER = { lat: -41.2, lng: 173.2 };
const NZ_ZOOM = 6;

/** A planned route to draw; the other sub-trips of a trip are drawn muted. */
export interface MapRoute {
  route: RouteResult;
  muted?: boolean;
}

/** A marker for a place that is not one of the open sub-trip's points, such as where another sub-trip ends. */
export interface ExtraMarker {
  lat: number;
  lng: number;
  label: string;
  title: string;
}

export function MapCanvas({
  points,
  routes,
  extraMarkers,
  onPin,
  focusRequest,
  busy = false,
  overview = false
}: {
  points: PlannedPoint[];
  routes?: MapRoute[];
  extraMarkers?: ExtraMarker[];
  /** Omitted for a read-only map: clicks do not drop pins. */
  onPin?: (location: SelectedLocation) => void;
  focusRequest?: { lat: number; lng: number; token: string };
  busy?: boolean;
  /** Read-only presentation that frames the whole route, without editing controls. */
  overview?: boolean;
}) {
  const mapElement = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | undefined>(undefined);
  const markers = useRef<google.maps.Marker[]>([]);
  const polylines = useRef<google.maps.Polyline[]>([]);
  const signalOverlay = useRef<SignalOverlay | undefined>(undefined);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [zoom, setZoom] = useState(NZ_ZOOM);
  /** Start time of the running traffic-light ping, 0 when none is running. */
  const [ping, setPing] = useState(0);
  const [speedView, setSpeedView] = useState(false);
  /** The speed-limit stretch under the pointer, and where the pointer is within the map. */
  const [hovered, setHovered] = useState<{ text: string; x: number; y: number }>();
  const frame = useRef<HTMLDivElement>(null);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const pinHandler = useRef(onPin);
  const interactive = Boolean(onPin);
  useEffect(() => { pinHandler.current = onPin; }, [onPin]);

  // Signals and speed limits belong to the open sub-trip; the overview shows neither.
  const openRoute = useMemo(() => overview ? undefined : routes?.find((entry) => !entry.muted)?.route, [overview, routes]);
  const signals = useMemo(() => openRoute ? signalMarkers(openRoute) : [], [openRoute]);
  const stretches = useMemo(() => openRoute ? speedStretches(openRoute) : [], [openRoute]);
  const showSpeeds = speedView && stretches.length > 0;

  useEffect(() => {
    if (!apiKey) {
      setStatus('missing');
      return;
    }
    let disposed = false;
    let clickListener: google.maps.MapsEventListener | undefined;
    let zoomListener: google.maps.MapsEventListener | undefined;
    void (async () => {
      try {
        const googleMaps = await loadGoogleMaps(apiKey);
        if (disposed || !mapElement.current) return;
        const instance = new googleMaps.maps.Map(mapElement.current, {
          center: NZ_CENTER,
          zoom: NZ_ZOOM,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          cameraControl: !overview,
          keyboardShortcuts: !overview,
          clickableIcons: false,
          gestureHandling: 'greedy'
        });
        map.current = instance;
        if (interactive) clickListener = instance.addListener('click', (event: google.maps.MapMouseEvent) => {
          if (!event.latLng) return;
          pinHandler.current?.({
            label: 'Dropped pin',
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
            source: 'pin'
          });
        });
        if (!overview) {
          zoomListener = instance.addListener('zoom_changed', () => setZoom(instance.getZoom() ?? NZ_ZOOM));
          signalOverlay.current = createSignalOverlay(googleMaps.maps);
          signalOverlay.current.setMap(instance);
        }
        setStatus('ready');
      } catch (error) {
        track('maps-error', { ...errorDetail(error), detail: { ...errorDetail(error).detail, component: 'map' } });
        reportUnexpected(error, { reason: 'map' });
        if (!disposed) setStatus('error');
      }
    })();
    return () => {
      disposed = true;
      clickListener?.remove();
      zoomListener?.remove();
      signalOverlay.current?.setMap(null);
      signalOverlay.current = undefined;
      markers.current.forEach((marker) => marker.setMap(null));
      polylines.current.forEach((line) => line.setMap(null));
    };
  }, [apiKey, interactive, overview]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || status !== 'ready') return;
    markers.current.forEach((marker) => marker.setMap(null));
    markers.current = [
      ...mapMarkers(points).map(({ point, label, title }) => ({ lat: point.lat, lng: point.lng, label, title })),
      ...(extraMarkers ?? [])
    ].map(({ lat, lng, label, title }) => new google.maps.Marker({ map: instance, position: { lat, lng }, label, title }));
  }, [points, extraMarkers, status]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || status !== 'ready') return;
    polylines.current.forEach((line) => { google.maps.event.clearInstanceListeners(line); line.setMap(null); });
    setHovered(undefined);
    // Muted routes first, so the open sub-trip's route is drawn on top.
    const lines = [...(routes ?? [])].sort((a, b) => Number(b.muted ?? false) - Number(a.muted ?? false))
      .filter(({ route }) => !(showSpeeds && route === openRoute))
      .map(({ route, muted }) => new google.maps.Polyline({
        map: instance,
        path: route.geometry.coordinates.map(([lng, lat]) => ({ lat, lng })),
        strokeColor: muted ? '#8d98a5' : '#dc5b36',
        strokeOpacity: muted ? 0.65 : 0.92,
        strokeWeight: muted ? 3 : 4
      }));
    // Each stretch of equal limit is its own line, so the pointer can tell which one it is over. In the speed view
    // the stretches are the open route, coloured, and hovering one picks out the whole stretch; otherwise they are
    // invisible, slightly wider hover targets over the plain route line, and the hovered stretch is drawn in its
    // colour by a separate line that ignores the pointer, so showing it cannot shrink the hover target.
    const style = showSpeeds ? { strokeOpacity: 0.95, strokeWeight: 5, zIndex: 1 } : { strokeOpacity: 0, strokeWeight: 14, zIndex: 3 };
    const highlight = new google.maps.Polyline({ clickable: false, strokeOpacity: 0.95, strokeWeight: 7, zIndex: 2 });
    lines.push(highlight);
    for (const stretch of stretches) {
      const line = new google.maps.Polyline({ map: instance, path: stretch.path, strokeColor: speedColor(stretch.kmh), ...style });
      const text = describeStretch(stretch);
      const point = (event: google.maps.MapMouseEvent) => {
        const box = frame.current?.getBoundingClientRect();
        const dom = event.domEvent as Partial<MouseEvent>;
        if (box && dom.clientX !== undefined && dom.clientY !== undefined) setHovered({ text, x: dom.clientX - box.left, y: dom.clientY - box.top });
      };
      const enter = (event: google.maps.MapMouseEvent) => {
        if (showSpeeds) line.setOptions({ strokeWeight: 10, zIndex: 2 });
        else highlight.setOptions({ map: instance, path: stretch.path, strokeColor: speedColor(stretch.kmh) });
        point(event);
      };
      line.addListener('mouseover', enter);
      line.addListener('click', enter);
      line.addListener('mousemove', point);
      line.addListener('mouseout', () => { line.setOptions(style); highlight.setMap(null); setHovered(undefined); });
      lines.push(line);
    }
    polylines.current = lines;
  }, [openRoute, routes, showSpeeds, status, stretches]);

  useEffect(() => {
    signalOverlay.current?.setSignals(signals);
  }, [signals, status]);

  useEffect(() => {
    signalOverlay.current?.setMode(ping ? 'ping' : zoom >= SIGNAL_MIN_ZOOM ? 'shown' : 'hidden');
  }, [ping, signals, status, zoom]);

  useEffect(() => {
    if (!ping) return;
    const timer = setTimeout(() => setPing(0), SIGNAL_PING_MS);
    return () => clearTimeout(timer);
  }, [ping]);

  // The overview always frames the whole trip; the planner leaves the viewport to the rider.
  useEffect(() => {
    const instance = map.current;
    if (!overview || !instance || status !== 'ready') return;
    const planned = (routes ?? []).flatMap(({ route }) => route.geometry.coordinates.map(([lng, lat]) => ({ lat, lng })));
    const path = planned.length > 0 ? planned : points;
    if (path.length === 0) return;
    const bounds = new google.maps.LatLngBounds();
    path.forEach((point) => bounds.extend({ lat: point.lat, lng: point.lng }));
    const fit = () => instance.fitBounds(bounds, 36);
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [overview, points, routes, status]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || status !== 'ready' || !focusRequest) return;
    instance.panTo({ lat: focusRequest.lat, lng: focusRequest.lng });
    instance.setZoom(14);
  }, [focusRequest, status]);

  const zoomBy = (amount: number) => {
    const instance = map.current;
    const currentZoom = instance?.getZoom();
    if (instance && currentZoom !== undefined) instance.setZoom(Math.max(2, Math.min(21, currentZoom + amount)));
  };

  const pingSignals = () => {
    track('traffic-lights-pinged', { detail: { signals: signals.length, zoom } });
    setPing(Date.now());
  };

  const toggleSpeedView = () => {
    track('speed-limits-toggled', { detail: { shown: !speedView, stretches: stretches.length } });
    setSpeedView(!speedView);
  };

  const signalLabel = signals.length
    ? `Show all ${signals.length} traffic light${signals.length === 1 ? '' : 's'} on this route (they also show from zoom ${SIGNAL_MIN_ZOOM})`
    : 'No mapped traffic lights on this route';

  return (
    <div className="map-frame" ref={frame}>
      <div ref={mapElement} className="map-canvas" />
      {hovered ? <div className={hovered.x > (frame.current?.clientWidth ?? 0) - 200 ? 'route-tooltip route-tooltip-left' : 'route-tooltip'}
        style={{ left: hovered.x, top: hovered.y }} aria-hidden="true">{hovered.text}</div> : null}
      {busy ? <div className="map-loading" role="status" aria-live="polite"><span className="loading-spinner" /> <span>Updating route…</span></div> : null}
      {status === 'ready' && !overview ? <div className="map-zoom-controls" aria-label="Map zoom controls">
        <button type="button" className="map-zoom-button" onClick={() => zoomBy(1)} aria-label="Zoom in">+</button>
        <button type="button" className="map-zoom-button" onClick={() => zoomBy(-1)} aria-label="Zoom out">−</button>
      </div> : null}
      {status === 'ready' && openRoute ? <div className="map-layer-controls" aria-label="Route layers">
        <button type="button" className="map-layer-button" onClick={pingSignals} disabled={!signals.length} aria-label={signalLabel} title={signalLabel}>
          <span className="map-layer-light" aria-hidden="true" />
          <span className="map-layer-count">{signals.length}</span>
        </button>
        <button type="button" className="map-layer-button" onClick={toggleSpeedView} aria-pressed={speedView} disabled={!stretches.length}
          aria-label="Colour the route by speed limit" title={stretches.length ? 'Colour the route by speed limit' : 'No speed-limit data for this route'}>
          <span className="map-layer-speed" aria-hidden="true">50</span>
        </button>
      </div> : null}
      {status === 'ready' && showSpeeds ? <div className="speed-legend">
        <div className="speed-legend-title">Speed limits <span>OpenStreetMap; check the signs</span></div>
        <div className="speed-legend-bands">
          {SPEED_BANDS.map((band) => <span key={band.label}><i style={{ background: band.color }} />{band.label}</span>)}
          <span><i style={{ background: UNKNOWN_SPEED_COLOR }} />Unmapped</span>
        </div>
        <div className="speed-legend-hover" aria-live="polite">{hovered?.text ?? 'Hover the route to see a stretch’s limit.'}</div>
      </div> : null}
      {status !== 'ready' ? (
        <div className="map-state">
          <div className="map-state-mark">⌁</div>
          <strong>{status === 'missing' ? 'Map setup needed' : status === 'loading' ? 'Loading map' : 'Map unavailable'}</strong>
          <p>
            {status === 'missing'
              ? 'Add a restricted Google Maps browser key to search places and drop pins.'
              : status === 'loading'
                ? 'Connecting to Google Maps…'
                : 'Check the browser key and Maps JavaScript API configuration.'}
          </p>
        </div>
      ) : null}
      <div className="map-credit">Google Maps · OSM routing data</div>
    </div>
  );
}
